export const COURSE_DEMO_UNLOCK_MESSAGE = "Unlocks after course demo";

/** Minimal org shape for Demo entitlement checks (avoids importing Prisma client in Vite). */
export type OrganisationWithDemoEntitlements = {
    id?: number;
    name?: string | null;
    orgType?: string | null;
    Tier?: { tier?: string | null } | null;
    CollectionEntitlements?: { storyCollectionId: number }[];
    licenceEndDate?: Date | string | null;
};

export const isDemoOrganisation = (organisation?: OrganisationWithDemoEntitlements | null): boolean =>
    organisation?.Tier?.tier === "Demo";

/** Personal org from a Verbal course-demo invite. Detected via orgType, not Demo tier or licenceEndDate. */
export const isIndividualOrganisation = (organisation?: OrganisationWithDemoEntitlements | null): boolean =>
    organisation?.orgType === "Individual";

/**
 * Solo UX (no Users, no staff invite, no view switcher, skip facilitator).
 * Keys off Individual org type — Demo multi-user orgs and Full+licenceEndDate orgs are not this.
 */
export const isSoloPersonalOrganisation = (
    organisation?: OrganisationWithDemoEntitlements | null,
    isSchool?: boolean,
): boolean => {
    if (isSchool) {
        return false;
    }
    return isIndividualOrganisation(organisation);
};

export const organisationLicenceHasExpired = (
    organisation?: OrganisationWithDemoEntitlements | null,
    isVerbal?: boolean,
): boolean => {
    if (isVerbal || organisation?.name === "Verbal") {
        return false;
    }
    if (!organisation?.licenceEndDate) {
        return false;
    }
    return new Date(organisation.licenceEndDate).getTime() <= Date.now();
};

export const licenceCongratulationsKey = (organisationId: number, licenceEndDate: Date | string): string => {
    const iso = new Date(licenceEndDate).toISOString();
    return `verbal.licenceCongratulations.${organisationId}.${iso}`;
};

/**
 * One-shot after Demo→Full on an Individual org. Not schools, Verbal, Demo,
 * expired licences, or Full orgs that are not Individual.
 */
export const shouldShowLicenceCongratulations = (
    organisation?: OrganisationWithDemoEntitlements | null,
    isSchool?: boolean,
): boolean => {
    if (isSchool || organisation?.name === "Verbal") {
        return false;
    }
    if (!organisation?.id || !organisation.licenceEndDate) {
        return false;
    }
    if (isDemoOrganisation(organisation) || organisationLicenceHasExpired(organisation)) {
        return false;
    }
    if (!isIndividualOrganisation(organisation)) {
        return false;
    }
    return localStorage.getItem(licenceCongratulationsKey(organisation.id, organisation.licenceEndDate)) !== "1";
};

export const markLicenceCongratulationsSeen = (organisationId: number, licenceEndDate: Date | string): void => {
    localStorage.setItem(licenceCongratulationsKey(organisationId, licenceEndDate), "1");
};

export const getEntitledCollectionIds = (organisation?: OrganisationWithDemoEntitlements | null): number[] =>
    organisation?.CollectionEntitlements?.map((entitlement) => entitlement.storyCollectionId) ?? [];

export const isCollectionEntitledForDemo = (
    collectionId: number,
    organisation?: OrganisationWithDemoEntitlements | null,
): boolean => {
    if (!isDemoOrganisation(organisation)) {
        return true;
    }
    return getEntitledCollectionIds(organisation).includes(collectionId);
};

export const curriculumHasDemoAccess = (
    collectionIds: number[] | undefined,
    organisation?: OrganisationWithDemoEntitlements | null,
): boolean => {
    if (!isDemoOrganisation(organisation)) {
        return true;
    }
    if (!collectionIds?.length) {
        return false;
    }
    const entitledIds = new Set(getEntitledCollectionIds(organisation));
    return collectionIds.some((id) => entitledIds.has(id));
};
